import { Component, Input, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { timeout } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { FirebaseService } from '../../services/firebase.service';
import { RaygunService } from '../../services/raygun.service';

/**
 * Handles the revertSecondFactorAddition email action. When a second factor is enrolled,
 * Firebase notifies the account's email with a revert link so a user whose account was
 * taken over can undo the enrollment. The link carries a one-time oob code; applying it
 * removes the newly added factor and revokes the account's sessions.
 */
@Component({
    selector: 'app-revert-second-factor',
    templateUrl: './revert-second-factor.component.html',
    styleUrls: ['./revert-second-factor.component.scss'],
    standalone: false
})
export class RevertSecondFactorComponent implements OnInit {

  @Input()
  public code: string; // Firebase oob code

  public checking = true;

  public email: string;

  public phoneNumber: string;

  public reverted = false;

  public errorMsg: string;

  public requestInProgress = false;

  public linkInvalid = false;

  /** Guards the exit link against a second click while the session check is out. */
  public leaving = false;

  /**
   * Shown when leaving the page fails. Kept apart from errorMsg, whose view is about the
   * link itself and offers a password reset - wrong advice for a navigation that did not
   * complete.
   */
  public leaveErrorMsg: string;

  constructor(private firebase: FirebaseService,
              private raygun: RaygunService,
              private authService: AuthService,
              private router: Router,
              private ngZone: NgZone) {}

  public async ngOnInit(): Promise<void> {
    try {
      const info = await this.firebase.auth.checkActionCode(this.code);
      if (info?.operation !== 'REVERT_SECOND_FACTOR_ADDITION') {
        throw { code: 'auth/invalid-action-code' };
      }
      this.email = info?.data?.email;
      this.phoneNumber = (info?.data as any)?.multiFactorInfo?.phoneNumber;
    } catch (e) {
      this.linkInvalid = true;
      this.handleError(e, 'Failed to verify the link from Firebase');
    } finally {
      this.checking = false;
    }
  }

  public async revert(): Promise<void> {
    this.errorMsg = null;
    this.requestInProgress = true;
    try {
      await this.firebase.auth.applyActionCode(this.code);
      this.reverted = true;
    } catch (e) {
      this.handleError(e, 'Failed to remove the second factor from Firebase');
      this.requestInProgress = false;
    }
  }

  /**
   * Leaves the page without reverting. This link is opened from the enrollment email, often
   * in the browser the user is already signed in on, so a live session belongs on the
   * dashboard - which is what every other route into public/login does, via
   * ExistingSessionGuard.
   *
   * That guard cannot do it here. This handler renders inside LoginComponent, so the route
   * is already public/login; dropping the oobCode is a query-param-only change on the same
   * path, and Angular's default runGuardsAndResolvers ('paramsChange') does not re-run
   * guards for one. The session is therefore checked explicitly.
   *
   * Only the pre-revert exit routes this way. The success view's link must stay on the
   * login page: applying the code revokes the account's sessions server-side, so the
   * dashboard would load against a token the backend rejects.
   */
  public async backToLogin(): Promise<void> {
    if (this.leaving) {
      return;
    }
    this.leaving = true;
    this.leaveErrorMsg = null;
    try {
      // The status subject holds `undefined` until the session bootstrap reports in, and
      // hasValidSession waits for that (and already ends with take(1)). 15s matches
      // LoginComponent.completeMfaLogin(), which waits on this same bootstrap.
      //
      // Deliberately no fallback branch: guessing "no session" on a timeout would send a
      // signed-in user to the login form, which is this bug itself. A stalled bootstrap is
      // reported instead of silently decided.
      const session = await this.authService
        .hasValidSession()
        .pipe(timeout(15000))
        .toPromise();
      // Firebase resolves the session bootstrap outside the Angular zone, so the navigation
      // has to re-enter it.
      //
      // The result is deliberately not checked. navigate() resolves false whenever the
      // navigation did not complete, and on the dashboard path that is the normal outcome,
      // not a failure: '/' is a redirect hub whose guards send a single-module user onward -
      // UserGuard calls router.navigate(['/main']) and returns false for every user type
      // except an internal one holding both permissions. An earlier version treated that
      // false as an error, so the correct redirect flashed "Something went wrong leaving
      // this page" on the way to the dashboard (Bug 47681, second round).
      await this.ngZone.run(
        () => this.router.navigate([session ? '/' : 'public/login']));
    } catch (e) {
      this.ngZone.run(() => {
        this.leaveErrorMsg = 'Something went wrong leaving this page. Please reload the page and try again.';
        this.raygun.send(e, { message: 'Failed to leave the revert second factor page' });
      });
    } finally {
      // Always, rather than only on the error path: a navigation that was cancelled without
      // throwing has to leave a usable button behind, and one that succeeded has destroyed
      // this component, so the write goes nowhere.
      this.ngZone.run(() => {
        this.leaving = false;
      });
    }
  }

  private handleError(e: any, raygunMessage: string): void {
    if (e?.code === 'auth/invalid-action-code' || e?.code === 'auth/expired-action-code') {
      this.errorMsg = 'This link has expired or was already used. If you still need to remove two-factor authentication, sign in and disable it from your profile settings.';
      return;
    }

    // Include the Firebase error code so a screenshot or a support ticket is diagnosable
    // without needing the browser console.
    this.errorMsg = e?.code
      ? `Something went wrong. Try again or please contact support. (${e.code})`
      : 'Something went wrong. Try again or please contact support.';
    this.raygun.send(e, { message: raygunMessage });
  }
}
